import React, { useCallback, useEffect, useState } from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { useTranslation } from 'react-i18next';
import { Box, Grid, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useHasClerkRole } from '../../common/hooks/useHasClerkRole';
import { useHasClerkPermissions } from '../../common/hooks/useHasClerkPermissions';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchEmployees, selectAllEmployees } from '../../slices/employees/employeesSlice';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { Employee } from '../../models/employee.model';
import styles from './EmployeeProfiles.module.scss';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { NWClient } from '../../client/NWClient';
import MessageModal from '../../common/message-modal/MessageModal';
import { useOrganization } from '@clerk/clerk-react';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
//import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import EmploymentInfoText from '../library/components/employment-info-text/EmploymentInfoText';
import Preview from './components/preview-block/Preview';
import { Helpers } from '../../common/helpers/helpers';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CategoryCard from '../../common/category-card/CategoryCard';
import CategoryCardGrid from '../../common/category-card/CategoryCardGrid';
import { CATEGORY_CARD_ACCENTS } from '../../common/category-card/types';

const EmployeeProfiles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [planMessageVisible, setPlanMessageVisible] = useState(false);
  const [deleteMessageModalOpen, setDeleteMessageModalOpen] = useState(false);
  const [employmentDataMessage, setEmploymentDataMessage] = useState(false);
  const company = useAppSelector(selectAllCompanies)[0];
  const employeesCreatedByAdmin = useAppSelector(selectAllEmployees);
  const employeesLoading = useAppSelector((state) => state.employees.status);
  // This logic is tied to company member employees which do not have a full_name attribute (created by sign up)

  const { invitations } = useOrganization({
    invitations: true,
    //memberships: true,
  });

  // Examples of how to use hooks
  const isAdmin = useHasClerkRole('org:admin');
  const hasEditPermissions = useHasClerkPermissions(['org:sys_memberships:manage']);

  const [employeeId, setEmployeeId] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const employeeUserId = employeesCreatedByAdmin?.find((el) => el.id === employeeId)?.user;

  const employeeJoinedCompany = Boolean(
    company?.members.find((el) => el.id === employeeUserId)?.user_uuid
  );

  const invitationList = invitations?.data;
  const employeeData = employeesCreatedByAdmin?.find((el) => el.id === employeeId);
  const invitation = invitationList?.find(
    (invite) =>
      invite.emailAddress === employeeData?.email_address ||
      invite.emailAddress === employeeData?.business_email_address
  );

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setEmployeeId(id);
  };
  const handleMoreVertClose = () => {
    setAnchorEl(null);
  };

  const handleProfileDelete = () => {
    if (employeeId) {
      NWClient.delete('employee', employeeId).then(() => {
        if (invitation) {
          invitation.revoke();
        }
        setDeleteMessageModalOpen(false);
        setEmployeeId(null);
        dispatch(fetchEmployees({ company_id: company?.id ?? 0 }));
        setAnchorEl(null);
      });
    }
  };

  const handleEditButton = (id: number) => {
    navigate(ROUTES.EMPLOYEE_PROFILES + '/' + id + '?initialTab=1');
  };

  const handleDocumentsButton = (id: number) => {
    navigate(ROUTES.EMPLOYEE_PROFILES + '/' + id + '?initialTab=0');
  };

  const handleCreateEmployee = () => {
    const planSettings = Helpers.getPlanSettings(company);
    if (!planSettings) return;

    const limit = planSettings.employee_profile;
    const count = employeesCreatedByAdmin?.length ?? 0;

    if (limit === 0) {
      setPlanMessageVisible(true);
    } else if (limit !== 'unlimited' && count >= Number(limit)) {
      setPlanMessageVisible(true);
    } else {
      navigate(ROUTES.EMPLOYEE_PROFILE_CREATE);
    }
  };

  const fetchEmployeesArray = useCallback(() => {
    if (employeesLoading === 'idle') {
      dispatch(fetchEmployees({ company_id: company?.id ?? 0 }));
    }
  }, [company, dispatch, employeesLoading]);

  useEffect(() => {
    fetchEmployeesArray();
  }, [fetchEmployeesArray]);

  return (
    <>
      <AppHeader
        title={
          <>
            <span style={{ marginRight: '8px' }}>{t('employeeProfiles.header')}</span>
            <PreviewTooltip title={<Preview />} arrow placement='top'>
              <InfoIcon fontSize='small' className={'info-icon'} />
            </PreviewTooltip>
          </>
        }
      />
      <Grid component='div' container>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignItems: 'flex-start',
            marginTop: '16px',
          }}
        >
          <Box className={styles.page_description}>{t('messages.employeeProfilesDescription')}</Box>
          {/*<Tooltip
            title={t('employmentTemplatesInfo.employmentDocumentGuidance')}
            placement='top'
            arrow
          >
            <Button
              id='employeeProfilesEmploymentDocumentGuidanceBtn'
              variant='contained'
              className='app-bar-link white-variant min-width-inherit'
              onClick={() => {
                setEmploymentDataMessage(true);
              }}
            >
              <PriorityHighIcon
                id='employeeProfilesEmploymentDocumentGuidanceBtnPriorityHighIcon'
                fontSize='medium'
              />
            </Button>
          </Tooltip>*/}
        </Box>
        <Grid component='div' size={{ xs: 12 }} className={styles.employee_cards}>
          <CategoryCardGrid id='employeeProfilesGrid'>
            {[...employeesCreatedByAdmin]
              .sort((a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? ''))
              .map((employee: Employee) => (
                <CategoryCard
                  id={`employeeProfilesEmployeesCreatedByAdminCard_${employee.id}`}
                  key={employee.id}
                  title={employee.full_name ?? ''}
                  subtitle={employee.employments?.[0]?.position_title?.trim() || undefined}
                  icon={<BadgeOutlinedIcon />}
                  accent={CATEGORY_CARD_ACCENTS.Employment}
                  onClick={() => navigate(ROUTES.EMPLOYEE_PROFILES + '/' + employee.id)}
                  actions={
                    <Box className={styles.employee_profile_icons}>
                      <Tooltip title={t('buttons.documents')} placement='top' arrow>
                        <IconButton
                          id={`employeeProfilesDocumentsButtonIconBtn_${employee.id}`}
                          aria-label={t('buttons.documents')}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDocumentsButton(employee.id);
                          }}
                          size='small'
                        >
                          <UploadFileOutlinedIcon
                            id={`employeeProfilesDocumentsButtonIconBtnUploadFileIcon_${employee.id}`}
                            fontSize='small'
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('buttons.edit')} placement='top' arrow>
                        {/* Wrapper keeps the tooltip working when the button is disabled */}
                        <span style={{ display: 'inline-flex' }}>
                          <IconButton
                            id={`employeeProfilesEditButtonIconBtn_${employee.id}`}
                            aria-label={t('buttons.edit')}
                            disabled={!hasEditPermissions}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleEditButton(employee.id);
                            }}
                            size='small'
                            sx={{ p: 0.5 }}
                          >
                            <EditIcon
                              id={`employeeProfilesEditButtonIconBtnEditIcon_${employee.id}`}
                              fontSize='small'
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton
                        id={`employeeProfilesMoreOptionsIconBtn_${employee.id}`}
                        aria-label={t('labels.menuButton')}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClick(event, employee.id);
                        }}
                        size='small'
                        sx={{ p: 0.5 }}
                      >
                        <MoreVertIcon
                          id={`employeeProfilesMoreOptionsIconBtnMoreVertIcon_${employee.id}`}
                          fontSize='small'
                        />
                      </IconButton>
                    </Box>
                  }
                />
              ))}
            <Button
              id='employeeProfilesAddProfileBtn'
              onClick={handleCreateEmployee}
              variant='contained'
              color='primary'
              className={styles.add_button}
              disabled={!isAdmin}
            >
              <Box
                id='employeeProfilesAddProfileBtnBox'
                sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: '1rem' }}
              >
                <Typography
                  id='employeeProfilesAddProfileBtnText'
                  sx={{ fontWeight: 'light', fontSize: 14 }}
                >
                  {t('buttons.addProfile')}
                </Typography>
                <AddIcon id='employeeProfilesAddProfileBtnAddIcon' />
              </Box>
            </Button>
          </CategoryCardGrid>
        </Grid>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMoreVertClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            id='employeeProfilesDeleteMenuItem'
            onClick={employeeJoinedCompany ? undefined : () => setDeleteMessageModalOpen(true)}
          >
            {employeeJoinedCompany
              ? t('buttons.employeeDeletionNotAvailable')
              : t('buttons.delete')}
          </MenuItem>
        </Menu>

        {planMessageVisible && (
          <MessageModal
            open={planMessageVisible}
            modalMessage={t('messages.limitEmployees')}
            actionButtonText={t('buttons.ok')}
            actionButtonFn={() => navigate(ROUTES.PRICING)}
            goBackButtonText={t('buttons.later')}
            onClose={() => {
              setPlanMessageVisible(false);
            }}
          />
        )}
        {employmentDataMessage && (
          <MessageModal
            open={employmentDataMessage}
            modalTitle={t('employmentTemplatesInfo.employmentDocumentGuidance')}
            modalContent={<EmploymentInfoText />}
            headerVariant={'h5'}
            goBackButtonText={t('buttons.close')}
            noActionButton={true}
            onClose={() => {
              setEmploymentDataMessage(false);
            }}
          />
        )}
        {deleteMessageModalOpen && (
          <MessageModal
            open={deleteMessageModalOpen}
            modalTitle={t('messages.deleteEmployee')}
            modalMessage={t('messages.deleteEmployeeDialog')}
            actionButtonText={t('buttons.goBack')}
            actionButtonFn={() => setDeleteMessageModalOpen(false)}
            goBackButtonText={t('buttons.delete')}
            onDismiss={() => setDeleteMessageModalOpen(false)}
            onClose={() => {
              handleProfileDelete();
            }}
          />
        )}
      </Grid>
    </>
  );
};

export default EmployeeProfiles;
